import useLayoutAwareVideoSettingsState from '@src/components/sidepanel/useLayoutAwareVideoSettingsState';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import DeleteIcon from '@src/svgs/DeleteIcon';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import parsePercent from '@src/utility/parsePercent';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isBarTarget } from './barGate';
import { useFloatingToolbarHost } from './FloatingToolbarHost';
import {
  FloatingToolbarSurface,
  ToolbarDivider,
  ToolbarIconButton,
  ToolbarMenu,
  ToolbarMenuItem,
  ToolbarTextButton,
} from './FloatingToolbarSurface';
import { ToolbarZoomStepper } from './ToolbarStepper';
import { ToolbarLayer } from './ToolbarLayer';
import { useFloatingToolbarPosition } from './useFloatingToolbarPosition';

import type { FloatingToolbarHost } from './FloatingToolbarHost';
import type { ElementState } from '@src/renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

// Volume presets keep the toolbar compact; the sidebar's free numeric input
// stays the precision control.
const VOLUME_OPTIONS = [100, 75, 50, 25, 0];

// Zoom mirrors VideoSettings' b-roll scale slider (20–500%, where the value is
// the element width). 10% steps make the stepper usable across that range.
const MIN_ZOOM = 20;
const MAX_ZOOM = 500;
const ZOOM_STEP = 10;

/**
 * Contextual toolbar for b-roll video elements. Surfaces the most-used
 * VideoSettings controls — Fit, Volume, Zoom, Delete — without opening the
 * right sidebar, plus a "More" affordance for the full panel.
 *
 * Gated to b-roll only: the original video track has no sidebar panel today
 * and its actionable set is unresolved (see PLAT-10405 open questions), so it
 * is intentionally excluded for now. Plain `video` elements only — stacked
 * layout compositions are deferred.
 */
const VideoFloatingToolbar = observer(() => {
  // PLAT-12101 — the surface's host; no host, no bar.
  const host = useFloatingToolbarHost();
  const activeElement = host?.getActiveElement();

  // PLAT-12098 — a single, top-level b-roll clip, not while playing (DE1 / DE2).
  const isBroll =
    !!host &&
    activeElement?.source?.type === 'video' &&
    isBarTarget(host) &&
    !host.isOriginalVideoElement(activeElement.source);

  if (!host || !isBroll || !activeElement) return null;

  // Keyed by element id so the layout-aware hook's state resets cleanly when
  // the selection moves between b-roll clips.
  return (
    <VideoToolbarInner
      key={activeElement.source.id}
      activeElement={activeElement}
      editorStore={host.editorStore}
      host={host}
    />
  );
});

export default VideoFloatingToolbar;

interface VideoToolbarInnerProps {
  activeElement: ElementState;
  editorStore: EditorStoreLike;
  host: FloatingToolbarHost;
}

const VideoToolbarInner = observer(
  ({ activeElement, editorStore, host }: VideoToolbarInnerProps) => {
    const elementId = activeElement.source.id;
    const { toolbarRef, pos } = useFloatingToolbarPosition(true, elementId);

    const {
      activeTimelineVideoElement,
      displayElement,
      updateTimelineVideoProperty,
      handleChangeScale,
    } = useLayoutAwareVideoSettingsState({
      activeElement,
      editorStore,
      layout: host.layout,
    });

    // Displayed values come from the element on every render, never from
    // local state, so a change from the sidebar or an undo shows here too
    // (see ImageFloatingToolbar). The layout-aware hook recomputes from the
    // editor's current elements, and this is an observer.
    const tl = activeTimelineVideoElement.source as Record<string, unknown>;
    const fit = typeof tl.fit === 'string' ? tl.fit : 'cover';
    const volume = parsePercent(tl.volume) ?? 100;
    const zoom = Math.round(parsePercent(displayElement.source.width) ?? 100);
    const [volumeOpen, setVolumeOpen] = useState(false);
    const [volumeMenuPos, setVolumeMenuPos] = useState<{
      top: number;
      left: number;
    } | null>(null);

    // A new selection starts with the menu closed.
    useEffect(() => {
      setVolumeOpen(false);
    }, [elementId]);

    // Close the volume menu on an outside click, except on its own trigger,
    // whose click toggles it.
    const volumeTriggerRef = useRef<HTMLButtonElement>(null);
    const volumeMenuRef = useRef<HTMLDivElement>(null);
    const closeVolumeOnOutside = useCallback((e: MouseEvent) => {
      const trigger = volumeTriggerRef.current;
      if (trigger && e.target instanceof Node && trigger.contains(e.target)) {
        return;
      }
      setVolumeOpen(false);
    }, []);
    useOutsideAlerter(volumeMenuRef, closeVolumeOnOutside);

    const applyFit = (next: string) =>
      updateTimelineVideoProperty({ fit: next });

    const applyVolume = (next: number) => {
      setVolumeOpen(false);
      // Match the sidebar, which sends the raw numeric value as a string.
      updateTimelineVideoProperty({ volume: String(next) });
    };

    const applyZoom = (next: number) => handleChangeScale(next);

    // Saved right after the command, as the chip's Delete is (PLAT-12098 DS3).
    const handleDelete = () => {
      editorStore.editor.commandRemoveElement(
        activeTimelineVideoElement.source.id,
      );
      host.persist();
    };

    // More toggles the floating Media > Video panel with this clip's full
    // settings, through the host (PLAT-12101) — see ImageFloatingToolbar.
    const { settings } = host;
    const isSidebarOnThisElement = !!settings?.isOpen('video');

    const volumeMenu =
      volumeOpen && volumeMenuPos ? (
        <ToolbarMenu
          ref={volumeMenuRef}
          style={{ top: volumeMenuPos.top, left: volumeMenuPos.left }}
          onMouseDown={e => e.stopPropagation()}
        >
          {VOLUME_OPTIONS.map(opt => (
            <ToolbarMenuItem
              key={opt}
              isSelected={opt === volume}
              onClick={() => applyVolume(opt)}
            >
              {opt}%
            </ToolbarMenuItem>
          ))}
        </ToolbarMenu>
      ) : null;

    return (
      <ToolbarLayer>
        <FloatingToolbarSurface ref={toolbarRef} pos={pos}>
          {/* Fit */}
          <ToolbarTextButton
            isActive={fit === 'cover'}
            onClick={() => applyFit('cover')}
          >
            Cover
          </ToolbarTextButton>
          <ToolbarTextButton
            isActive={fit === 'contain'}
            onClick={() => applyFit('contain')}
          >
            Contain
          </ToolbarTextButton>

          <ToolbarDivider />

          {/* Volume */}
          <ToolbarTextButton
            ref={volumeTriggerRef}
            isActive={volumeOpen}
            aria-label="Volume"
            onClick={e => {
              if (volumeOpen) {
                setVolumeOpen(false);
              } else {
                const r = e.currentTarget.getBoundingClientRect();
                setVolumeMenuPos({ top: r.bottom + 6, left: r.left });
                setVolumeOpen(true);
              }
            }}
          >
            {volume}%
          </ToolbarTextButton>

          <ToolbarDivider />

          {/* Zoom */}
          <ToolbarZoomStepper
            value={zoom}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            onChange={applyZoom}
          />

          <ToolbarDivider />

          {/* Delete */}
          <ToolbarIconButton aria-label="Delete" onClick={handleDelete}>
            <DeleteIcon width="15" height="15" strokeColor="currentColor" />
          </ToolbarIconButton>

          {/* More — toggle the full settings panel */}
          {settings && (
            <ToolbarIconButton
              aria-label="More options"
              isActive={isSidebarOnThisElement}
              onClick={() => settings.toggle('video')}
            >
              <EllipsisIcon width="16" height="4" fillColor="currentColor" />
            </ToolbarIconButton>
          )}
        </FloatingToolbarSurface>
        {volumeMenu}
      </ToolbarLayer>
    );
  },
);
